import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, Search } from 'lucide-react';

import { api, ErroApi } from '@/lib/api';
import { apenasDigitos, cnsValido, cpfValido } from '@/lib/mascaras';
import { ROTULOS_ORIGEM, type PacientePec, type SituacaoPec } from '@/features/pec/tipos';
import { Botao } from '@/components/ui/button';

interface BuscaPecProps {
  cpf: string;
  cns: string;
  /** Recebe os dados encontrados para preencher o formulário. */
  aoEncontrar: (paciente: PacientePec) => void;
}

/**
 * Botão de consulta ao cadastro do PEC.
 *
 * Só aparece quando a integração está configurada e no ar — um botão que falha
 * a cada clique é pior do que botão nenhum. A consulta é sempre opcional: se o
 * paciente não estiver no PEC, ou se a integração cair, o operador segue
 * digitando normalmente.
 */
export function BuscaPec({ cpf, cns, aoEncontrar }: BuscaPecProps) {
  const [mensagem, setMensagem] = React.useState<{
    tom: 'sucesso' | 'erro';
    texto: string;
  } | null>(null);

  const { data: situacao } = useQuery({
    queryKey: ['pec-situacao'],
    queryFn: () => api.get<SituacaoPec>('/pec/situacao'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const buscar = useMutation({
    mutationFn: () => {
      const parametros = new URLSearchParams();
      if (cpfValido(cpf)) parametros.set('cpf', apenasDigitos(cpf));
      else if (cnsValido(cns)) parametros.set('cns', apenasDigitos(cns));

      return api.get<PacientePec>(`/pec/paciente?${parametros.toString()}`);
    },
    onSuccess: (paciente) => {
      aoEncontrar(paciente);
      setMensagem({
        tom: 'sucesso',
        texto:
          `Dados carregados do ${ROTULOS_ORIGEM[paciente.origem]}. ` +
          'Confira e informe o telefone.',
      });
    },
    onError: (erro) => {
      setMensagem({
        tom: 'erro',
        texto:
          erro instanceof ErroApi
            ? erro.mensagemUsuario
            : 'Não foi possível consultar o PEC. Preencha os dados manualmente.',
      });
    },
  });

  // Integração desligada ou fora do ar: nem exibe o botão.
  if (!situacao?.ativo || !situacao.disponivel) return null;

  const documentoValido = cpfValido(cpf) || cnsValido(cns);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Botao
          type="button"
          variante="contorno"
          tamanho="sm"
          onClick={() => {
            setMensagem(null);
            buscar.mutate();
          }}
          disabled={!documentoValido || buscar.isPending}
        >
          {buscar.isPending ? (
            <Loader2 className="animate-spin" aria-hidden="true" />
          ) : (
            <Search aria-hidden="true" />
          )}
          {buscar.isPending ? 'Consultando…' : 'Buscar dados no PEC'}
        </Botao>

        {!documentoValido && (
          <span className="text-silenciado-texto text-xs">
            Informe um CPF ou CNS válido para consultar.
          </span>
        )}
      </div>

      {mensagem && (
        <p
          role="status"
          className={`flex items-start gap-1.5 text-xs ${
            mensagem.tom === 'sucesso' ? 'text-sucesso' : 'text-alerta'
          }`}
        >
          {mensagem.tom === 'sucesso' ? (
            <CheckCircle2 className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          )}
          {mensagem.texto}
        </p>
      )}
    </div>
  );
}
